import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Overlay, Sam17Avatar, Chip } from "./ContactUI";
import { SAM } from "./phaseD";
import { avatarUrl } from "./wildrushPlates";

// Sam17 profile — temporary exploration detour. Plausible, age-neutral, no realistic person,
// no age shown, no planted "clue" (Master Design Screen 14: the child can only conclude "I don't know").
export const ProfileSheet = ({ onBack, backLabel = "← Back" }) => (
  <Overlay label="Sam17 profile" testid="profile-sheet" onDismiss={onBack}>
    <div className="flex items-center gap-4">
      <Sam17Avatar size={64} />
      <div>
        <p className="text-xl font-semibold" data-testid="profile-name">{SAM}</p>
        <p className="text-sm text-white/60" data-testid="profile-meta">Level 42 · Member since 2024</p>
      </div>
    </div>
    <div className="mt-5 rounded-2xl bg-white/6 p-4">
      <p className="text-[11px] font-semibold tracking-[0.18em] text-white/45">BIO</p>
      <p className="mt-1 text-[15px] text-white/85" data-testid="profile-bio">football ⚽ | racing 🎮 | music 🎧</p>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs text-white/60">
      <div className="rounded-xl bg-white/6 py-2"><p className="text-base font-semibold text-white/90">6</p>maps</div>
      <div className="rounded-xl bg-white/6 py-2"><p className="text-base font-semibold text-white/90">1.2k</p>checkpoints</div>
      <div className="rounded-xl bg-white/6 py-2"><p className="text-base font-semibold text-white/90">Public</p>profile</div>
    </div>
    <div className="mt-5"><Chip id="profile-back" label={backLabel} icon={ArrowLeft} quiet onClick={onBack} /></div>
  </Overlay>
);

// Fictional simulated gallery (Master 25D / Branching §12): abstract game-avatar images only. No camera,
// no file chooser, no upload. Selecting one represents the act of sharing inside the simulation.
const TILES = [{ id: "visor", label: "teal helmet" }, { id: "fox", label: "sand helmet" }, { id: "bolt", label: "green hood" }, { id: "orbit", label: "orange goggles" }];
export const Gallery = ({ onPick, onBack }) => (
  <Overlay label="Choose game avatar image" testid="simulated-gallery" onDismiss={onBack}>
    <p className="text-[11px] font-semibold tracking-[0.18em] text-white/55">WILD RUSH</p>
    <p className="mt-1 text-lg font-semibold">Choose game avatar image</p>
    <div className="mt-4 grid grid-cols-2 gap-3">
      {TILES.map((t) => (
        <button key={t.id} onClick={() => onPick(t.id)} aria-label={`Send ${t.label} avatar image`} data-testid={`gallery-${t.id}`}
          className="flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-[#0f2a33] transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4FA3D9]">
          <img src={avatarUrl(t.id)} alt="" aria-hidden="true" draggable={false} className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
    <div className="mt-4"><Chip id="gallery-back" label="Back" icon={ArrowLeft} quiet onClick={onBack} /></div>
  </Overlay>
);

// Safeguarding terminal screen (Phase H2A). Calm, no blame, no continue/practise-again/report/feedback.
// Copy approved by the user; final safeguarding/legal review required before public launch (PRD).
export const SafeguardingClose = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <div className="flex h-[100dvh] w-full items-center justify-center bg-[#0a0a0a] px-6 text-white" data-testid="wr-safeguarding-terminal">
      <div className="max-w-md">
        <p className="font-display text-3xl font-semibold" data-testid="wr-safeguarding-title">Let’s pause here.</p>
        <p className="mt-5 text-base leading-relaxed text-white/75" data-testid="wr-safeguarding-body">
          What you’ve said matters. Please talk to an adult you trust — a parent, carer, teacher, or another grown-up who looks after you — and tell them what’s going on.
        </p>
        <p className="mt-4 text-base leading-relaxed text-white/75" data-testid="wr-safeguarding-close-line">You can close this practice now.</p>
        <button onClick={() => { onClose(); navigate("/simulations"); }} data-testid="wr-safeguarding-close" className="mt-8 rounded-full border border-white/20 px-7 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10">Close</button>
      </div>
    </div>
  );
};
